import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { flushSync } from 'react-dom';
import type {
  Action,
  AgentConfig,
  ContentBlock,
  HostContextItem,
  InitMessage,
  ResumeMessage,
  TokenMessage,
  ViewportMessage,
} from '@beidou/agent-protocol';
import { acquireSessionToken, buildSessionStorageKey } from '@beidou/agent-protocol';
import { DefaultGlyphIcon } from './components/DefaultGlyphIcon';
import { MessageBlocks } from './components/MessageBlocks';
import { ConfirmDialog } from './components/ConfirmDialog';
import { MemoryPanel } from './components/MemoryPanel';
import { MessageFeedbackBar } from './components/MessageFeedbackBar';
import { ReasoningPanel } from './components/ReasoningPanel';
import { ResultPane } from './components/ResultPane';
import { ToastStack } from './components/ToastStack';
import { handleAction, useChat, type ChatMessage } from './hooks/useChat';
import { useToast } from './hooks/useToast';
import type { AgentThemeTokens } from '@beidou/agent-protocol';
import { applyThemeFromConfig, buildAgentTheme, resolveThemeVariant, type ThemeVariant } from './theme/cyan-beam';
import {
  extractFollowUpChips,
  extractResultBlocks,
  filterChatColumnBlocks,
  filterRenderableBlocks,
  getAutoScrollSignature,
} from './utils/chatUi';
import {
  ENGINE_HINTS,
  ENGINE_LABELS,
  loadStoredEngine,
  normalizeEngineChoice,
  storeEngine,
  type AgentEngine,
} from './utils/agentEngine';
import { itemTypeLabel } from './utils/itemTypes';
import { fetchSessionHistory } from './utils/sessionRestore';
import { applySafeAreaInsets, installSafeAreaInsets } from './utils/safeArea';
import { installKeyboardInsetTracker, setHostKeyboardInset, setHostBottomInset } from './utils/keyboardInset';
import { installKeyboardDismissOnGesture } from './utils/dismissKeyboardOnGesture';
import { notifyParentComposerKeyboard } from './utils/parentKeyboard';
import type { CopyFormat } from './utils/copyContent';

const DEFAULT_API =
  import.meta.env.VITE_AGENT_API_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8900');
const DEV_APP_ID = import.meta.env.VITE_DEV_APP_ID || 'tspx-013';
const DEV_CONTEXT_ITEM_TYPE = import.meta.env.VITE_DEV_CONTEXT_ITEM_TYPE || '';
// 生产构建下 import.meta.env.DEV 静态求值为 false，兜底 key 不会进产物
const DEV_API_KEY =
  import.meta.env.VITE_DEV_API_KEY || (import.meta.env.DEV ? 'dev-key-tspx-013' : '');
const TOKEN_REFRESH_TIMEOUT_MS = 15_000;

export type AppLayout = 'panel' | 'sidebar' | 'split';
export type UiRole = 'user' | 'dev' | 'ops';

function isStandaloneDev(): boolean {
  return import.meta.env.DEV && window.parent === window;
}

/**
 * 嵌入模式下 token 由宿主 SDK 持有（api-key 也只在宿主侧），
 * 这里只能请求宿主重新换取，并等它把新 token 投递回来。
 */
function requestParentToken(): Promise<string | null> {
  if (typeof window === 'undefined' || window.parent === window) {
    return Promise.resolve(null);
  }
  const requestId = `rt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  return new Promise((resolve) => {
    let settled = false;
    const finish = (token: string | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onToken);
      window.clearTimeout(timer);
      resolve(token);
    };
    const onToken = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      const data = event.data as TokenMessage | null;
      if (!data || data.channel !== 'beidou-agent' || data.type !== 'token') return;
      if (data.requestId && data.requestId !== requestId) return;
      finish(data.token || null);
    };
    const timer = window.setTimeout(() => finish(null), TOKEN_REFRESH_TIMEOUT_MS);
    window.addEventListener('message', onToken);
    // 宿主页 origin 因租户而异，无法固定；这条消息不含任何凭据
    window.parent.postMessage(
      { channel: 'beidou-agent', type: 'refresh-token', requestId },
      '*'
    );
  });
}

function resolveDevLayout(): AppLayout {
  const raw = new URLSearchParams(window.location.search).get('layout');
  if (raw === 'split' || raw === 'sidebar' || raw === 'panel') return raw;
  return 'panel';
}

/** 等浏览器完成绘制后再通知宿主关加载层，避免 ready 后仍空白一截 */
function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

function resolveInitialUiRole(): UiRole {
  const raw = new URLSearchParams(window.location.search).get('ui-role');
  if (raw === 'dev' || raw === 'ops' || raw === 'user') return raw;
  if (isStandaloneDev()) return 'dev';
  return 'user';
}

/** 底栏建议 chips 上限：超出会折行把输入框往下挤 */
const MAX_COMPOSER_CHIPS = 4;

function isContentBlocks(content: ChatMessage['content']): content is ContentBlock[] {
  return Array.isArray(content);
}

function configLabel(cfg: AgentConfig): string {
  return cfg.context_item_type ? `${cfg.app_id}（上下文: ${cfg.context_item_type}）` : cfg.app_id;
}

function contextFollowUpChips(ctx: HostContextItem | null): { label: string; message: string }[] {
  if (!ctx?.title || !ctx?.item_id) return [];
  // 文案仍用标题；真正的种子 id 由 chat body.context_item 传给后端
  const label = itemTypeLabel(ctx.item_type);
  return [{ label: '找类似的', message: `推荐和《${ctx.title}》类似的${label}` }];
}

function applyAgentEngineFromConfig(
  cfg: AgentConfig,
  setAllowed: Dispatch<SetStateAction<AgentEngine[]>>,
  setEngine: Dispatch<SetStateAction<AgentEngine>>,
) {
  const ae = cfg.agent_engine;
  if (!ae) return;
  const allowed = (ae.allowed?.length ? ae.allowed : ['legacy']) as AgentEngine[];
  setAllowed(allowed);
  setEngine((prev) =>
    normalizeEngineChoice(prev, allowed, (ae.default as AgentEngine) || 'legacy')
  );
}

export function App() {
  const [config, setConfig] = useState<AgentConfig | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [apiUrl, setApiUrl] = useState(DEFAULT_API);
  const [embedded, setEmbedded] = useState(false);
  const [layout, setLayout] = useState<AppLayout>(() => resolveDevLayout());
  const [uiRole, setUiRole] = useState<UiRole>(() => resolveInitialUiRole());
  const [contextItem, setContextItem] = useState<HostContextItem | null>(null);
  /** 宿主页当前内容类型；随每轮 chat 上报，不参与 session 身份 */
  const [contextItemType, setContextItemType] = useState<string | null>(
    DEV_CONTEXT_ITEM_TYPE || null
  );
  const [input, setInput] = useState('');
  const [initError, setInitError] = useState<string | null>(null);
  const [themeVariant, setThemeVariant] = useState<ThemeVariant>('default');
  const [themeTokens, setThemeTokens] = useState<AgentThemeTokens>(() => buildAgentTheme());
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [memoryRefreshKey, setMemoryRefreshKey] = useState(0);
  const [newChatConfirmOpen, setNewChatConfirmOpen] = useState(false);
  const [rateLimitSec, setRateLimitSec] = useState(0);
  const [editingMsgId, setEditingMsgId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [allowedEngines, setAllowedEngines] = useState<AgentEngine[]>(['legacy', 'react', 'shadow']);
  const [agentEngine, setAgentEngine] = useState<AgentEngine>(() => loadStoredEngine('legacy'));
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const autoScrollSigRef = useRef('');
  /** 用户是否贴着列表底部；上滑阅读时不再强行滚底 */
  const stickToBottomRef = useRef(true);
  /** 已完成过一次会话恢复，同 token 的重复 init 不再拉历史 */
  const sessionBootRef = useRef<{ token: string; apiUrl: string } | null>(null);
  /** 防止并发 restoreSession / 重复 request-init 风暴 */
  const restoreInFlightRef = useRef(false);
  /** 首轮 restore Promise：后续 soft init 必须 await，避免过早 ready 关掉宿主加载层 */
  const restorePromiseRef = useRef<Promise<void> | null>(null);
  const chatBusyRef = useRef(false);
  const configRef = useRef<AgentConfig | null>(null);
  const applyInitRef = useRef<(data: InitMessage) => Promise<void>>(async () => {});
  const applyResumeRef = useRef<(data: ResumeMessage) => void>(() => {});
  const prevLoadingRef = useRef(false);
  const devInitStarted = useRef(false);
  const { toasts, showToast, dismiss } = useToast();

  const showEnginePicker = uiRole === 'dev' || uiRole === 'ops';

  const refreshDevToken = useCallback(async (): Promise<string | null> => {
    if (!DEV_API_KEY) return null;
    try {
      const authBody: Record<string, string> = { app_id: DEV_APP_ID, user_id: 'dev-user' };
      if (DEV_CONTEXT_ITEM_TYPE) authBody.context_item_type = DEV_CONTEXT_ITEM_TYPE;
      const { token } = await acquireSessionToken({
        apiUrl,
        apiKey: DEV_API_KEY,
        authBody,
        storageKey: buildSessionStorageKey({
          appId: DEV_APP_ID,
          userId: 'dev-user',
          apiUrl,
        }),
        forceNew: true,
      });
      return token;
    } catch {
      return null;
    }
  }, [apiUrl]);

  /** 统一的换 token 入口：嵌入模式找宿主要，独立开发模式自己换 */
  const refreshToken = useCallback(async (): Promise<string | null> => {
    const next = embedded ? await requestParentToken() : await refreshDevToken();
    if (!next) return null;
    setToken(next);
    setSessionExpired(false);
    return next;
  }, [embedded, refreshDevToken]);

  const {
    messages,
    loading,
    status,
    sessionExpired,
    setSessionExpired,
    sendMessage,
    regenerateLast,
    editAndResend,
    stopGeneration,
    showWelcome,
    loadHistory,
    toggleReasoning,
    setFeedbackRating,
    resetConversation,
  } = useChat(apiUrl, token, {
    engine: showEnginePicker ? agentEngine : null,
    uiRole,
    contextItem,
    contextItemType,
    onRefreshToken: refreshToken,
    onError: (msg) => showToast(msg, 'error', 4000),
    onRateLimited: (sec) => {
      setRateLimitSec(sec);
      showToast(`请求过于频繁，${sec} 秒后可重试`, 'warn', sec * 1000);
    },
  });

  chatBusyRef.current =
    loading || messages.some((m) => m.role === 'user' || (m.id !== 'welcome' && m.role === 'assistant'));
  configRef.current = config;

  useEffect(() => {
    if (rateLimitSec <= 0) return;
    const t = window.setInterval(() => {
      setRateLimitSec((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(t);
  }, [rateLimitSec]);

  const feedbackEnabled = config?.ui?.feedback?.enabled !== false;
  const memoryPanelEnabled = config?.ui?.memory_panel?.enabled !== false;
  const copyFormats = useMemo(() => {
    const copyCfg = config?.ui?.copy;
    const formats: CopyFormat[] = [];
    if (copyCfg?.plain !== false) formats.push('plain');
    if (copyCfg?.markdown !== false) formats.push('markdown');
    if (copyCfg?.json !== false) formats.push('json');
    return formats;
  }, [config?.ui?.copy]);

  useEffect(() => {
    if (prevLoadingRef.current && !loading && memoryPanelEnabled) {
      setMemoryRefreshKey((k) => k + 1);
    }
    prevLoadingRef.current = loading;
  }, [loading, memoryPanelEnabled]);

  const showReasoningDetail =
    uiRole === 'dev' || uiRole === 'ops' || config?.ui?.reasoning?.show_detail_panel !== false;
  const isSplitLayout = layout === 'split';
  const resultBlocks = useMemo(
    () => (isSplitLayout ? extractResultBlocks(messages) : []),
    [isSplitLayout, messages]
  );

  const applyTheme = useCallback((cfg: AgentConfig | null, mode?: string, isEmbedded?: boolean) => {
    const variant = resolveThemeVariant({ config: cfg, themeMode: mode, embedded: isEmbedded });
    setThemeVariant(variant);
    const tokens = applyThemeFromConfig(document.documentElement, cfg, {
      themeMode: mode,
      embedded: isEmbedded,
    });
    setThemeTokens(tokens);
    document.documentElement.classList.toggle('embed-glass', Boolean(isEmbedded));
  }, []);

  const restoreSession = useCallback(
    async (authToken: string, cfg: AgentConfig) => {
      if (restorePromiseRef.current) {
        await restorePromiseRef.current;
        return;
      }
      const run = (async () => {
        restoreInFlightRef.current = true;
        try {
          let result = await fetchSessionHistory(apiUrl, authToken);
          if (result.status === 'unauthorized') {
            // 服务端 session 已消失（重启 / TTL / flush）：趁用户还没开口就换掉
            const fresh = await refreshToken();
            result = fresh
              ? await fetchSessionHistory(apiUrl, fresh)
              : { status: 'empty' };
          }
          if (chatBusyRef.current) {
            // 用户已发言 / 正在生成：迟到的 restore 不得冲掉本地聊天
            return;
          }
          // 同步刷进 DOM，避免随后立刻 post ready 时画面还是空的
          flushSync(() => {
            if (result.status === 'ok') {
              loadHistory(result.messages);
            } else {
              showWelcome(cfg);
            }
          });
        } finally {
          restoreInFlightRef.current = false;
          restorePromiseRef.current = null;
        }
      })();
      restorePromiseRef.current = run;
      await run;
    },
    [apiUrl, loadHistory, refreshToken, showWelcome]
  );

  const applyInit = useCallback(
    async (data: InitMessage) => {
      const nextApi = data.apiUrl || DEFAULT_API;
      const prev = sessionBootRef.current;
      const softOnly =
        Boolean(prev) && prev!.token === data.token && prev!.apiUrl === nextApi;

      setConfig(data.config);
      setToken(data.token);
      setApiUrl(nextApi);
      setEmbedded(true);
      setLayout(data.layout || 'panel');
      setUiRole(data.uiRole || 'user');
      setContextItem(data.contextItem || null);
      setContextItemType(data.contextItemType || null);
      setInitError(null);
      applyTheme(data.config, data.themeMode, true);
      applyAgentEngineFromConfig(data.config, setAllowedEngines, setAgentEngine);

      // 重复 init（例如误发）：只更新软状态，但仍需等首轮 restore 完成再 ready
      if (!softOnly) {
        // 先占坑，避免 await 期间又来一次 init 并发拉历史、冲掉聊天
        sessionBootRef.current = { token: data.token, apiUrl: nextApi };
        await restoreSession(data.token, data.config);
        // 首次载入历史后贴底一次即可
        stickToBottomRef.current = true;
      } else if (restorePromiseRef.current) {
        await restorePromiseRef.current;
      }
      // 等欢迎语/历史真正画上再关宿主加载层
      await waitForNextPaint();
      window.parent.postMessage({ channel: 'beidou-agent', type: 'ready' }, '*');
    },
    [applyTheme, restoreSession]
  );

  const applyResume = useCallback(
    (data: ResumeMessage) => {
      // 只在值真的变了才 setState，避免无意义重绘（打开面板时尤其明显）
      if (data.layout) {
        setLayout((prev) => (prev === data.layout ? prev : data.layout || 'panel'));
      }
      if (data.uiRole) {
        setUiRole((prev) => (prev === data.uiRole ? prev : (data.uiRole as UiRole) || 'user'));
      }
      if (data.contextItem !== undefined) {
        const next = data.contextItem || null;
        setContextItem((prev) => {
          if (
            (prev?.item_id || '') === (next?.item_id || '') &&
            (prev?.title || '') === (next?.title || '') &&
            (prev?.item_type || '') === (next?.item_type || '')
          ) {
            return prev;
          }
          return next;
        });
      }
      if (data.contextItemType !== undefined) {
        const nextType = data.contextItemType || null;
        setContextItemType((prev) => (prev === nextType ? prev : nextType));
      }
      if (data.token) {
        setToken((prev) => (prev === data.token ? prev : data.token || null));
      }
      setEmbedded((prev) => (prev ? prev : true));
      if (data.config) {
        const next = data.config;
        setConfig((prev) => {
          if (!prev) return next;
          const a = prev.theme;
          const b = next.theme;
          if (
            prev.welcome_message === next.welcome_message &&
            a?.primary_color === b?.primary_color &&
            a?.background_color === b?.background_color &&
            a?.icon === b?.icon &&
            a?.bot_name === b?.bot_name &&
            a?.welcome_message === b?.welcome_message
          ) {
            return prev;
          }
          return next;
        });
        applyTheme(next, data.themeMode, true);
      } else if (data.themeMode && configRef.current) {
        applyTheme(configRef.current, data.themeMode, true);
      }
    },
    [applyTheme]
  );

  applyInitRef.current = applyInit;
  applyResumeRef.current = applyResume;

  useEffect(() => {
    applyTheme(config, undefined, embedded);
  }, [applyTheme, config, embedded]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      // 只认宿主窗口，页面里其它 frame 不能伪造 init 把会话导去别的 apiUrl
      if (event.source !== window.parent) return;
      const data = event.data as InitMessage | ResumeMessage | ViewportMessage;
      if (!data || data.channel !== 'beidou-agent') return;
      if (data.type === 'viewport') {
        setHostKeyboardInset(
          Number(data.keyboardInset) || 0,
          Boolean((data as ViewportMessage).keyboardResolved)
        );
        setHostBottomInset(Number((data as ViewportMessage).hostBottomInset) || 0);
        const forceMobile =
          document.documentElement.classList.contains('embed-glass') ||
          window.parent !== window;
        applySafeAreaInsets({ forceMobileFallback: forceMobile });
        return;
      }
      if (data.type === 'init') {
        void applyInitRef.current(data);
        return;
      }
      if (data.type === 'resume') {
        applyResumeRef.current(data);
      }
    };
    window.addEventListener('message', handler);
    if (window.parent !== window) {
      // 仅挂载时请求一次；勿绑在 applyInit 依赖上，否则会与 setConfig 形成 request-init 死循环
      window.parent.postMessage({ channel: 'beidou-agent', type: 'request-init' }, '*');
    }
    return () => window.removeEventListener('message', handler);
  }, []);

  useEffect(() => {
    if (embedded || !isStandaloneDev() || devInitStarted.current) return;
    devInitStarted.current = true;
    (async () => {
      try {
        const cfgParams = new URLSearchParams({ app_id: DEV_APP_ID });
        if (DEV_CONTEXT_ITEM_TYPE) cfgParams.set('context_item_type', DEV_CONTEXT_ITEM_TYPE);
        const cfgResp = await fetch(`${DEFAULT_API}/v1/config?${cfgParams}`);
        if (!cfgResp.ok) throw new Error(`config ${cfgResp.status}`);
        const cfg = (await cfgResp.json()) as AgentConfig;
        const authBody: Record<string, string> = { app_id: DEV_APP_ID, user_id: 'dev-user' };
        if (DEV_CONTEXT_ITEM_TYPE) authBody.context_item_type = DEV_CONTEXT_ITEM_TYPE;
        const { token } = await acquireSessionToken({
          apiUrl: DEFAULT_API,
          apiKey: DEV_API_KEY,
          authBody,
          storageKey: buildSessionStorageKey({
            appId: DEV_APP_ID,
            userId: 'dev-user',
            apiUrl: DEFAULT_API,
          }),
        });
        setConfig(cfg);
        setToken(token);
        setApiUrl(DEFAULT_API);
        setInitError(null);
        applyAgentEngineFromConfig(cfg, setAllowedEngines, setAgentEngine);
        applyTheme(cfg, undefined, false);
        await restoreSession(token, cfg);
        sessionBootRef.current = { token, apiUrl: DEFAULT_API };
      } catch (e) {
        const msg = e instanceof Error ? e.message : '未知错误';
        setInitError(msg);
      }
    })();
  }, [embedded, applyTheme, restoreSession]);

  const onMessagesScroll = useCallback(() => {
    const el = messagesRef.current;
    if (!el) return;
    const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = gap < 96;
  }, []);

  useEffect(() => {
    const sig = getAutoScrollSignature(messages, loading);
    if (sig === autoScrollSigRef.current) return;
    autoScrollSigRef.current = sig;
    // 用户上滑查看历史时不要拽回底部；流式输出仅在仍贴底时跟随
    if (!stickToBottomRef.current) return;
    messagesEndRef.current?.scrollIntoView({
      behavior: loading ? 'auto' : 'smooth',
      block: 'end',
    });
  }, [messages, loading]);

  const onAction = useCallback(
    (action: Action) => {
      stickToBottomRef.current = true;
      handleAction(action, sendMessage);
    },
    [sendMessage]
  );

  const scrollFollowSend = useCallback(() => {
    stickToBottomRef.current = true;
  }, []);

  const onSend = () => {
    if (!input.trim() || rateLimitSec > 0) return;
    scrollFollowSend();
    sendMessage(input.trim());
    setInput('');
  };

  const onRegenerate = useCallback(() => {
    scrollFollowSend();
    regenerateLast();
  }, [regenerateLast, scrollFollowSend]);

  const onEditSend = useCallback(
    (messageId: string, text: string) => {
      scrollFollowSend();
      editAndResend(messageId, text);
    },
    [editAndResend, scrollFollowSend]
  );

  const onEngineChange = (value: string) => {
    const next = normalizeEngineChoice(value, allowedEngines, agentEngine);
    setAgentEngine(next);
    storeEngine(next);
    showToast(`引擎已切换为 ${ENGINE_LABELS[next]}`, 'success', 2500);
  };

  const onClose = () => {
    window.parent.postMessage({ channel: 'beidou-agent', type: 'close' }, '*');
  };

  const executeNewChat = useCallback(() => {
    setNewChatConfirmOpen(false);
    if (loading) stopGeneration();
    if (!config) {
      showToast('新对话失败：配置未就绪', 'error', 4000);
      return;
    }
    // 乐观更新：本地立刻进入欢迎页，再后台同步 session reset
    setInput('');
    showWelcome(config);
    showToast('已开始新对话', 'success', 2000);

    const syncReset = () => {
      void resetConversation().then((ok) => {
        if (ok) return;
        showToast('会话同步失败，可重试', 'error', {
          durationMs: 8000,
          actionLabel: '重试',
          onAction: () => syncReset(),
        });
      });
    };
    syncReset();
  }, [loading, stopGeneration, resetConversation, config, showWelcome, showToast]);

  const onNewChat = useCallback(() => {
    const hasUserMessages = messages.some((m) => m.role === 'user');
    if (hasUserMessages) {
      setNewChatConfirmOpen(true);
      return;
    }
    executeNewChat();
  }, [messages, executeNewChat]);

  useEffect(() => {
    if (!embedded) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [embedded]);

  /** 全屏嵌入时避开系统状态栏 / Home Indicator（含 Android 不回传 safe-area 的回退） */
  useEffect(() => {
    return installSafeAreaInsets({ forceMobileFallback: embedded });
  }, [embedded]);

  /** 软键盘弹出时把底部输入区顶到键盘之上 */
  useEffect(() => {
    return installKeyboardInsetTracker();
  }, []);

  /** 键盘打开时滑动 agent 页则收起键盘，避免 iOS 拖动露出宿主页 */
  useEffect(() => {
    const root = appRef.current;
    if (!root) return;
    return installKeyboardDismissOnGesture(root, {
      ignoreWithin: () => [composerRef.current],
    });
  }, []);

  const botName = config?.theme.bot_name || 'AI 助手';
  const welcomeSuggestions = config?.suggested_prompts || config?.theme.suggested_prompts || [];
  const followUpChips = useMemo(() => extractFollowUpChips(messages), [messages]);
  const ctxChips = useMemo(() => contextFollowUpChips(contextItem), [contextItem]);
  const composerChips = useMemo(() => {
    // 详情页的「找类似的」始终置顶保留，不被回复里的 follow_up 挤掉
    const merged = [...ctxChips];
    const seen = new Set(merged.map((c) => c.label));
    const append = (chips: { label: string; message: string }[]) => {
      for (const chip of chips) {
        if (merged.length >= MAX_COMPOSER_CHIPS) return;
        if (seen.has(chip.label)) continue;
        seen.add(chip.label);
        merged.push(chip);
      }
    };

    if (followUpChips.length > 0) {
      append(followUpChips.map((c) => ({ label: c.label, message: c.message })));
    } else if (messages.length === 1) {
      // messages 为空表示会话尚未恢复：此时只留 ctx chip，不铺欢迎建议
      append(welcomeSuggestions.map((s) => ({ label: s, message: s })));
    }
    return merged;
  }, [followUpChips, ctxChips, messages.length, welcomeSuggestions]);

  const lastAssistantIdx = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && messages[i].id !== 'welcome') return i;
    }
    return -1;
  }, [messages]);

  const renderBlocks = useCallback(
    (msg: ChatMessage) => {
      if (!isContentBlocks(msg.content)) return null;
      const blocks = isSplitLayout ? filterChatColumnBlocks(msg.content) : filterRenderableBlocks(msg.content);
      return (
        <MessageBlocks
          blocks={blocks}
          onAction={onAction}
          markdownStreaming={Boolean(msg.streaming && msg.markdownStreaming)}
        />
      );
    },
    [isSplitLayout, onAction]
  );

  const chatColumn = (
    <>
      {!embedded && (
        <div className="standalone-hint">
          {initError
            ? `连接失败：${initError}`
            : token
              ? `独立开发 — ${config ? configLabel(config) : DEV_APP_ID} · ${ENGINE_LABELS[agentEngine]}${isSplitLayout ? ' · split' : ''}`
              : `连接中 ${DEFAULT_API}...`}
        </div>
      )}
      {embedded && !token && (
        <div className="standalone-hint">
          {initError ? `连接失败：${initError}` : '正在连接 Agent…'}
        </div>
      )}

      {sessionExpired && (
        <div className="reconnect-banner">
          <span>会话已过期</span>
          <button type="button" className="suggestion-chip" onClick={() => void refreshToken()}>
            重新连接
          </button>
        </div>
      )}

      <div className="header">
        {themeTokens.iconKind === 'image' && themeTokens.iconSrc ? (
          <div className="avatar avatar--image" title={themeTokens.iconId}>
            <img src={themeTokens.iconSrc} alt="" width={30} height={30} draggable={false} />
          </div>
        ) : (
          <div className="avatar avatar--glyph" title={themeTokens.iconId}>
            <DefaultGlyphIcon />
          </div>
        )}
        <div className="header-info">
          <div className="bot-name">{botName}</div>
          <div className="bot-status">
            {contextItem?.title ? `正在帮你看：《${contextItem.title}》` : token ? '● 在线' : '○ 未连接'}
          </div>
        </div>
        {showEnginePicker && token && allowedEngines.length > 1 && (
          <label className="engine-select-wrap" title={ENGINE_HINTS[agentEngine]}>
            <span className="engine-select-label">引擎</span>
            <select
              className="engine-select"
              value={agentEngine}
              onChange={(e) => onEngineChange(e.target.value)}
              disabled={loading}
              aria-label="Agent 引擎"
            >
              {allowedEngines.map((eng) => (
                <option key={eng} value={eng} title={ENGINE_HINTS[eng]}>
                  {ENGINE_LABELS[eng]}
                </option>
              ))}
            </select>
          </label>
        )}
        {token && memoryPanelEnabled && (
          <button
            type="button"
            className="header-icon-btn"
            onClick={() => setMemoryOpen(true)}
            title="偏好记忆"
            aria-label="偏好记忆"
          >
            🧠
          </button>
        )}
        {token && (
          <button
            type="button"
            className="header-new-chat"
            onClick={onNewChat}
            title="新对话"
            aria-label="新对话"
          >
            ＋
          </button>
        )}
        {embedded && (
          <button type="button" className="header-close" onClick={onClose} title="关闭" aria-label="关闭">
            ×
          </button>
        )}
      </div>

      {status && <div className="status-bar status-bar--global">{status}</div>}

      <div className="messages" ref={messagesRef} onScroll={onMessagesScroll}>
        {messages.map((msg, idx) => (
          <div key={msg.id} className={`msg ${msg.role}`}>
            <div className="msg-body">
              {msg.role === 'user' ? (
                <div className="user-msg-wrap">
                  {editingMsgId === msg.id ? (
                    <div className="user-edit-box">
                      <textarea
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        rows={2}
                      />
                      <div className="user-edit-actions">
                        <button
                          type="button"
                          className="suggestion-chip"
                          onClick={() => {
                            onEditSend(msg.id, editDraft.trim());
                            setEditingMsgId(null);
                          }}
                        >
                          发送
                        </button>
                        <button type="button" className="suggestion-chip" onClick={() => setEditingMsgId(null)}>
                          取消
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="msg-bubble">{typeof msg.content === 'string' ? msg.content : ''}</div>
                      {!loading && typeof msg.content === 'string' && (
                        <div className="user-msg-actions">
                          <button
                            type="button"
                            className="feedback-btn msg-action-btn"
                            title="编辑消息"
                            aria-label="编辑消息"
                            onClick={() => {
                              setEditingMsgId(msg.id);
                              setEditDraft(msg.content as string);
                            }}
                          >
                            ✎
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : isContentBlocks(msg.content) ? (
                <div className={`bot-reply-panel${msg.id === 'welcome' ? ' bot-reply-panel--compact' : ''}`}>
                  {showEnginePicker && msg.turnEngine && msg.id !== 'welcome' && (
                    <div className="bot-reply-header">
                      <div className="engine-badge" title={msg.engineStatus || msg.turnEngine}>
                        {ENGINE_LABELS[msg.turnEngine as AgentEngine] || msg.turnEngine}
                      </div>
                    </div>
                  )}
                  {(msg.reasoning || msg.streaming) && (
                    <ReasoningPanel
                      reasoning={
                        msg.reasoning ?? { plan: '', tool: '', observe: '', sql: '', critic: '', summary: '' }
                      }
                      expanded={msg.reasoningExpanded ?? false}
                      onToggle={() => toggleReasoning(msg.id)}
                      streaming={msg.streaming}
                      streamingLabel={msg.progressLabel}
                      showDetail={showReasoningDetail}
                      latencyMs={msg.latencyMs}
                      usage={msg.usage}
                    />
                  )}
                  {renderBlocks(msg)}
                  {!msg.streaming && feedbackEnabled && msg.serverMessageId && msg.id !== 'welcome' && (
                    <MessageFeedbackBar
                      apiUrl={apiUrl}
                      token={token}
                      messageId={msg.serverMessageId}
                      traceId={msg.traceId}
                      content={filterRenderableBlocks(msg.content)}
                      copyFormats={copyFormats}
                      rating={msg.feedbackRating}
                      onRatingChange={(rating) => setFeedbackRating(msg.id, rating)}
                      onToast={showToast}
                      showRegenerate={idx === lastAssistantIdx}
                      onRegenerate={onRegenerate}
                      regenerateDisabled={loading || rateLimitSec > 0}
                    />
                  )}
                  {!msg.streaming &&
                    !feedbackEnabled &&
                    idx === lastAssistantIdx &&
                    msg.id !== 'welcome' && (
                    <div className="msg-action-row">
                      <button
                        type="button"
                        className="suggestion-chip"
                        disabled={loading || rateLimitSec > 0}
                        onClick={onRegenerate}
                      >
                        ↻ 重新生成
                      </button>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="composer" ref={composerRef}>
        {composerChips.length > 0 && (
          <div className="suggestions">
            {composerChips.map((chip) => (
              <button
                key={chip.label}
                type="button"
                className="suggestion-chip"
                onClick={() => {
                  scrollFollowSend();
                  // 立刻滚到底，让用户感知「点了就有反馈」，再发消息
                  messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                  void sendMessage(chip.message);
                }}
                disabled={!token || loading || rateLimitSec > 0}
              >
                {chip.label}
              </button>
            ))}
          </div>
        )}
        <div className="input-row">
          <div className="composer-leading">
            <button type="button" className="composer-icon-btn" disabled title="语音输入（即将推出）" aria-label="语音输入">
              🎤
            </button>
            <button type="button" className="composer-icon-btn" disabled title="以图搜片（即将推出）" aria-label="以图搜片">
              📷
            </button>
          </div>
          <textarea
            rows={1}
            value={input}
            placeholder={
              rateLimitSec > 0
                ? `${rateLimitSec}s 后可发送…`
                : token
                  ? '输入消息...'
                  : '等待 SDK 初始化...'
            }
            disabled={!token || loading || rateLimitSec > 0}
            onFocus={(e) => {
              if (embedded) notifyParentComposerKeyboard(true);
              stickToBottomRef.current = true;
              const el = e.currentTarget;
              window.setTimeout(() => {
                el.scrollIntoView({ block: 'end', behavior: 'smooth' });
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
              }, 120);
            }}
            onBlur={() => {
              if (embedded) notifyParentComposerKeyboard(false);
            }}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
          />
          {loading ? (
            <button type="button" className="send-btn send-btn--stop" onClick={stopGeneration} aria-label="停止生成">
              ■
            </button>
          ) : (
            <button
              type="button"
              className="send-btn"
              disabled={!token || !input.trim() || rateLimitSec > 0}
              onClick={onSend}
              aria-label="发送"
            >
              ↑
            </button>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div
      ref={appRef}
      className={`app layout-${layout}${themeVariant === 'embed-lite' ? ' app--embed-lite' : ''}${layout === 'sidebar' ? ' layout-sidebar' : ''}`}
    >
      <ToastStack toasts={toasts} onDismiss={dismiss} />
      <MemoryPanel
        apiUrl={apiUrl}
        token={token}
        open={memoryOpen}
        refreshKey={memoryRefreshKey}
        onClose={() => setMemoryOpen(false)}
        onToast={showToast}
        onSendMessage={(message) => {
          setMemoryOpen(false);
          scrollFollowSend();
          void sendMessage(message);
        }}
      />
      <ConfirmDialog
        open={newChatConfirmOpen}
        title="开始新对话"
        message="确定开始新对话？当前聊天记录将被清空。"
        confirmLabel="确定"
        cancelLabel="取消"
        onConfirm={() => executeNewChat()}
        onCancel={() => setNewChatConfirmOpen(false)}
      />
      {isSplitLayout ? (
        <div className="split-shell">
          <div className="chat-column">{chatColumn}</div>
          <ResultPane
            blocks={resultBlocks}
            onAction={onAction}
            suggestedPrompts={welcomeSuggestions}
            onSendMessage={sendMessage}
          />
        </div>
      ) : (
        chatColumn
      )}
    </div>
  );
}
